import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";

// Import your new, enhanced editor
import ParameterDefinitionEditor from "./ParameterDefinitionEditor";

export default function DynamicJenkinsParametersPage() {
  const [jsonInput, setJsonInput] = useState("");
  const [parsedJson, setParsedJson] = useState(null);
  const [parameterDefinitions, setParameterDefinitions] = useState([]);
  const [paramValues, setParamValues] = useState({});

  // Parse JSON and extract param definitions
  const handleParseJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setParsedJson(parsed);

      const propertyArray = parsed.property || [];
      const paramProperty = propertyArray.find((prop) => prop.parameterDefinitions);

      if (paramProperty && paramProperty.parameterDefinitions) {
        const definitions = paramProperty.parameterDefinitions;
        setParameterDefinitions(definitions);

        const initValues = {};
        definitions.forEach((def) => {
          initValues[def.name] = def.defaultParameterValue?.value ?? "";
        });
        setParamValues(initValues);
      } else {
        setParameterDefinitions([]);
        alert("No parameterDefinitions found in the provided JSON.");
      }
    } catch (error) {
      console.error(error);
      alert("Invalid JSON input");
    }
  };

  // Called when we "save changes" from ParameterDefinitionEditor
  const handleDefinitionsUpdate = (updatedDefs) => {
    setParameterDefinitions(updatedDefs);
    // Optionally update paramValues if new names or defaults changed
  };

  // Called whenever the user interacts with the rendered form
  const handleParamValueChange = (name, value) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  };

  // Renders each parameter with the appropriate UI
  const renderParameterField = (paramDef, idx) => {
    const { name, type, defaultParameterValue, choices } = paramDef;
    const defaultValue = defaultParameterValue?.value ?? "";
    const currentValue = paramValues[name] ?? "";

    let inputComponent = null;

    switch (type) {
      case "CascadeChoiceParameter": {
        // multi-select
        const safeCurrentValue = Array.isArray(currentValue)
          ? currentValue
          : currentValue
          ? [currentValue]
          : [];
        const choicesArray = Array.isArray(choices)
          ? choices.map((c) => ({ label: c, value: c }))
          : [];
        inputComponent = (
          <MultiSelect
            value={safeCurrentValue}
            options={choicesArray}
            onChange={(e) => handleParamValueChange(name, e.value)}
            placeholder={`Select ${name}`}
            display="chip"
          />
        );
        break;
      }

      case "ChoiceParameterDefinition": {
        const singleSelectOptions = Array.isArray(choices)
          ? choices.map((c) => ({ label: c, value: c }))
          : [];
        inputComponent = (
          <Dropdown
            value={currentValue}
            options={singleSelectOptions}
            onChange={(e) => handleParamValueChange(name, e.value)}
            placeholder={`Select ${name}`}
          />
        );
        break;
      }

      case "StringParameterDefinition": {
        inputComponent = (
          <InputText
            value={currentValue}
            onChange={(e) => handleParamValueChange(name, e.target.value)}
            placeholder={defaultValue}
          />
        );
        break;
      }

      case "NumberParameterDefinition": {
        inputComponent = (
          <InputNumber
            value={Number(currentValue)}
            onValueChange={(e) => handleParamValueChange(name, e.value)}
          />
        );
        break;
      }

      case "BooleanParameterDefinition": {
        inputComponent = (
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId={`boolParam-${idx}`}
              checked={Boolean(currentValue)}
              onChange={(e) => handleParamValueChange(name, e.checked)}
            />
            <label htmlFor={`boolParam-${idx}`}>Check me</label>
          </div>
        );
        break;
      }

      /* ================================
         NEW CUSTOM TYPES
         ================================ */
      case "Builds": {
        // Custom UI for "Builds" param type
        // For example, maybe it's a dropdown of build numbers you fetch, or a text field
        // We'll do something simple for demonstration:
        inputComponent = (
          <div>
            <span style={{ marginRight: "0.5rem" }}>Select Build:</span>
            <InputText
              value={currentValue}
              onChange={(e) => handleParamValueChange(name, e.target.value)}
              placeholder="Enter build number"
            />
          </div>
        );
        break;
      }

      case "Device": {
        // Another custom UI for "Device"
        // E.g., a multi-select of device IDs from an external source
        // We'll do something simple:
        inputComponent = (
          <div>
            <span style={{ marginRight: "0.5rem" }}>Device ID:</span>
            <InputText
              value={currentValue}
              onChange={(e) => handleParamValueChange(name, e.target.value)}
              placeholder="Enter device ID"
            />
          </div>
        );
        break;
      }

      default: {
        // fallback
        inputComponent = (
          <InputText
            value={currentValue}
            onChange={(e) => handleParamValueChange(name, e.target.value)}
            placeholder={defaultValue}
          />
        );
        break;
      }
    }

    return (
      <div
        key={idx}
        style={{
          display: "grid",
          gridTemplateColumns: "200px 1fr 200px",
          gap: "1rem",
          alignItems: "center",
          marginBottom: "1rem"
        }}
      >
        <label style={{ fontWeight: "bold" }}>{name}</label>
        {inputComponent}
        <span style={{ color: "gray", fontStyle: "italic" }}>
          Default: {defaultValue.toString()}
        </span>
      </div>
    );
  };

  return (
    <div style={{ padding: "2rem" }}>
      <h2>Dynamic Jenkins Parameters</h2>

      <p>Paste your Jenkins JSON below and click Parse JSON:</p>
      <div className="p-inputgroup" style={{ marginBottom: "1rem" }}>
        <textarea
          rows={8}
          style={{ width: "100%", fontFamily: "monospace" }}
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          placeholder="Paste Jenkins JSON here..."
        />
      </div>
      <Button label="Parse JSON" onClick={handleParseJson} />

      <hr style={{ margin: "2rem 0" }} />

      {/* Parameter Definitions Editor (Add/Remove/Reorder) */}
      {parameterDefinitions.length > 0 && (
        <ParameterDefinitionEditor
          paramDefinitions={parameterDefinitions}
          onUpdate={handleDefinitionsUpdate}
        />
      )}

      <h3>Generated UI</h3>
      {parameterDefinitions.length === 0 && (
        <p>No parameter definitions. Paste valid JSON and parse.</p>
      )}

      {parameterDefinitions.map(renderParameterField)}

      {/* Additional Jenkins info if present */}
      {parsedJson && (
        <div style={{ marginTop: "2rem" }}>
          <h4>Other JSON Info</h4>
          <p>
            <strong>Next Build Number:</strong> {parsedJson.nextBuildNumber}
          </p>
        </div>
      )}
    </div>
  );
}
